import type { TranslationObj } from "$/utils/lang"

import type { ICoachingSessionCoachModel } from "./coach.model.types"
import { ECohortModality, type IAttendanceSessionCohortModel, ICohortAvailableTagModel } from "./cohort.model.types"
import type { IEnrollmentModel } from "./enrollemnt.model.types"
import type { IAttendanceParticipantModuleModel } from "./module.model.types"

enum ESessionInvitationStatus {
    Accepted = "accepted",
    Pending = "pending",
    Declined = "rejected",
    Tentative = "tentative",
    Present = "present",
    NotPresent = "not_present"
}

enum ESessionAttendanceStatus {
    Present = "present",
    NotPresent = "not_present"
}

enum ESessionModality {
    Group = "group",
    Individual = "individual"
}

enum ESessionEventStatus {
    Scheduled = "scheduled",
    NotScheduled = "not_scheduled",
    Canceled = "canceled",
    Rescheduled = "rescheduled"
}

enum ESessionTypeSlug {
    IndividualBonus = "individual_bonus",
    IndividualCoaching = "individual_coaching",
    IndividualOnboarding = "individual_onboarding",
    IndividualLeadershipCoaching = "individual_leadership_coaching",
    IndividualLeadershipOnboarding = "individual_leadership_onboarding",
    IndividualExecutiveOnboarding = "individual_executive_onboarding",
    IndividualExecutiveCoaching = "individual_executive_coaching",
    GroupOnboarding = "group_onboarding",
    GroupCoaching = "group_coaching",
    GroupLeadershipOnboarding = "group_leadership_onboarding",
    GroupLeadershipCoaching = "group_leadership_coaching",
    GroupExecutiveOnboarding = "group_executive_onboarding",
    GroupExecutiveCoaching = "group_executive_coaching",
    SpotCoachingWorkshop = "spot_coaching_workshop"
}

enum ESessionTypeName {
    BonusSession = "Bonus Session",
    InitialCoachingSession = "Initial Coaching Session",
    IndividualCoaching = "Individual Coaching",
    IndividualExecutiveOnboarding = "Individual Executive Onboarding",
    IndividualExecutiveCoaching = "Individual Executive Coaching",
    CoachingSession = "Coaching Session",
    OnboardingSession = "Onboarding Session",
    GroupExecutiveOnboarding = "Group Executive Onboarding",
    GroupExecutiveCoaching = "Group Executive Coaching",
    SpotCoachingLab = "Spot Coaching Lab"
}

interface ISessionParticipantModel {
    firstName: string
    id: number
    isGuest: boolean
    journeyLength: number
    lastName: string
    photo: string | null
}

interface ICoachingSessionParticipantModel extends ISessionParticipantModel {
    attendanceStatus: ESessionAttendanceStatus
    invitationStatus: ESessionInvitationStatus
}

enum ESessionExtraPartyRole {
    Hr = "hr",
    DirectReport = "direct-report",
    Peer = "peer",
    Manager = "manager",
    Participant = "participant"
}

interface ISessionExtraPartyModel {
    id: number
    email: string | null
    firstName: string | null
    lastName: string | null
    label: string
    role: ESessionExtraPartyRole
    invitationStatus: ESessionInvitationStatus | null
    attendanceStatus: ESessionAttendanceStatus | null
}

interface ISessionRescheduleRecordModel {
    id: number
    dateFrom: string
    dateTo: string
    rescheduledAt: string
    rescheduledBy: string
}

interface ISessionBaseModel {
    allowedMinDaysToEdit: number
    isRescheduleAllowed: boolean
    coachId: number | null
    cohortModality: ECohortModality
    cancelledAt: string | null
    cancelledBy: string | null
    enrollmentRank: number
    extraParties: ISessionExtraPartyModel[] | []
    isExtraPartySession: boolean
    isGift: boolean
    isGifted: boolean
    isMissed: boolean
    isManual: boolean
    isCancelled: boolean
    joinUrl: string | undefined
    modality: ESessionModality
    productTypeId: number
    productTypeInternalName: string
    rescheduleHistory: ISessionRescheduleRecordModel[]
    schedulingWindowStart: string | null
    schedulingWindowEnd: string | null
    sessionId: number
    sessionTime: string | null
    sessionDuration: number
    sessionSlug: ESessionTypeSlug
    sessionName: ESessionTypeName | string
    preferredSessionName: string | null
}

interface ICoachingSessionModel extends ISessionBaseModel {
    isSwapAllowed: boolean
    cohortName: string
    coach: ICoachingSessionCoachModel
    groupId: number | null
    groupName: string | null
    participantId: number
    eventStatus: ESessionEventStatus
    isAttendanceMarked: boolean
    moduleId: number | null
    moduleTitle: TranslationObj | null
    isModuleReflectionPassed: boolean
    participants: ICoachingSessionParticipantModel[]
}

interface IAttendanceSessionParticipantModel extends ISessionParticipantModel {
    assignedFocusTag: ICohortAvailableTagModel | null
    nextEnrollment: IEnrollmentModel
    attendanceStatus: ESessionAttendanceStatus
    enrollments: IEnrollmentModel[]
    modules: IAttendanceParticipantModuleModel[]
}

interface IAttendanceSessionModel extends Omit<ISessionBaseModel, "cohortName" | "cohortModality"> {
    id: number
    isLast: boolean
    cohort: IAttendanceSessionCohortModel
    participants: IAttendanceSessionParticipantModel[]
}

interface IGroupSessionSummaryConfigModel {
    isSummaryAvailable: boolean
    areModulesEnabled: boolean
    withSummaryUnavailableWarning: boolean
}

interface ICoachPaginatedSessionModel extends ISessionBaseModel {
    id: number
    participants: ISessionParticipantModel[]
    groupName: string
    cohortName: string
}

export type {
    ISessionParticipantModel,
    ICoachingSessionParticipantModel,
    ISessionExtraPartyModel,
    ISessionRescheduleRecordModel,
    ISessionBaseModel,
    ICoachingSessionModel,
    IAttendanceSessionParticipantModel,
    IAttendanceSessionModel,
    ICoachPaginatedSessionModel,
    IGroupSessionSummaryConfigModel
}
export {
    ESessionExtraPartyRole,
    ESessionInvitationStatus,
    ESessionAttendanceStatus,
    ESessionModality,
    ESessionTypeSlug,
    ESessionTypeName,
    ESessionEventStatus
}
